import React from 'react';
import xtype from 'xtypejs'
import { DATE_FORMATS, NUMBER_FORMATS, INVALID_COORDINATE, INVALID_NUMBER } from '../../../helpers/parsers.js';
import {DATA_TYPE_PROP_MAP} from '../../common/AgGrid/index.jsx';
import { Parser, ERROR_DIV_ZERO, ERROR } from "hot-formula-parser";
import useDynamicCols from '../hooks/useDynamicCols.jsx';

window.parser = new Parser();

const parser = new Parser();
parser.setFunction("SPLIT_PART", (params) => {
  if ( typeof params[1] !== 'string' || typeof params[2] !== 'number'){
    return 'FORMULA_ERROR'
  }
  if ( typeof params[0] !== 'string' ) {
    return '';
  }

  const split = params[0].split(params[1]);
  return split[params[2]] || ''
})

const STATUSES = {
  error: 'error',
  warning: 'warning',
  valid: 'valid'
}

const validateBlank = (value) => {
  return !(xtype.isBlankString(value) || xtype.isNull(value));
}

const validateDataType = (data_type, value) => {

  // Always allow null (not blank)
  if (xtype.isNull(value)) {
    return true;
  }

  switch (data_type) {
    // User input
    case 'integer':
      return xtype.isInteger(value);

    case 'decimal':
      if (xtype.isInteger(value)) {
         return true;
      } else {
        return xtype.isFloat(value);
      }

    case 'boolean':
      return (/^(true|false)$/).test(value.toString().toLowerCase());

    case 'date':
      return dayjs(value, 'YYYY-MM-DD').isValid();

    case 'timestamp':
      return dayjs(value, 'YYYY-MM-DD HH:mm Z').isValid();

    case 'text':
      return !value.includes(INVALID_COORDINATE) && !value.includes(INVALID_NUMBER);

    // System provided
    case 'status':
      return xtype.isString(value);

    case 'recorded_at':
      return dayjs(value, 'YYYY-MM-DD HH:mm Z').isValid();

    case 'last_modified_at':
      return dayjs(value, 'YYYY-MM-DD HH:mm Z').isValid();
  }
  return true
};


const validationMessage = (data_type, status) => {
  switch (status) {
    case STATUSES.error:
      return I18n.t('invalid_datatype_error', {data_type: I18n.t(`data_type_${data_type}`)});
    case STATUSES.warning:
      return I18n.t('invalid_datatype_warning', {data_type: I18n.t(`data_type_${data_type}`)});
    // ..
  }
};


const valueParser = (value, data_type, columnSettings) => {
  const dataTypeProps = DATA_TYPE_PROP_MAP[data_type];

  var inputFormat = 'auto';
  var outputFormat = null;

  if (['date', 'timestamp'].includes(data_type)) {
    if (columnSettings.date_format) {
      inputFormat = columnSettings.date_format;
    }
    outputFormat = dataTypeProps.cellEditorParams.format;

  } else if (['decimal', 'integer'].includes(data_type) && columnSettings.number_format) {
    inputFormat = columnSettings.number_format;
  }

  if (columnSettings.preprocess_function && value) {
    parser.setVariable('CELL', value);
    var parseResult = parser.parse(columnSettings.preprocess_function);
    if (parseResult.error) {
      value = ''
    } else {
      value = parseResult.result;
    }
  }

  const gridParserFunc = dataTypeProps?.valueParser;
  if (!gridParserFunc) return ((value) => {console.warn("No value parser provided for ", value); return value;})
  return gridParserFunc({newValue: value, inputFormat: inputFormat, outputFormat: outputFormat})
};


// Used to map nested object values to get all columns inside header groups
const flatMapDeep = (value) => {
  return [].concat(...value.map(v => (v.marryChildren ? flatMapDeep(v.children) : v)));
};


const parseValue = (value) => {
  return value == 0 || !_.isUndefined(value) ? value : '';
};

const numberFormatOptions = [
  ...[{label: 'Auto', value: 'auto'}],
  ...$.map(NUMBER_FORMATS, (value, format) => ({label: value.example, value: format}))
];


const getInitialColumnDefs = (datagrid, settings, excel_col_names, showRowColumnCount = true) => {
  const columnDefs = [];

  if (showRowColumnCount) {
    columnDefs.push({
      field: 'excel-#',
      filter: 'agNumberColumnFilter',
      headerName: '#',
      resizable: true,
      sortable: false,
      filter: false,
      pinned: 'left',
      width: 80,
      suppressHeaderContextMenu: true,
      valueGetter: (params) => parseInt(params.node.id) + 1
    });
  }

  excel_col_names.forEach((column, i) => {
    columnDefs.push({
      field: `col${i+1}-e`,
      headerName: column.col_name,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.data[i]||''
    });
  });

  flatMapDeep(useDynamicCols(datagrid.columns || [], {supressBulkActions: true})).forEach((column, i) => {
    columnDefs.push({
      ...column
    });
  });
  return columnDefs;
};

// const isStepFailed = (gridApi, columnKey, range_from) => {
//   let exists = false;
//   let index = 0
//   // Because query all nodes so need to skip the row that contain headers
//   let minIndex = isNaN(parseInt(range_from)) ? 0 : parseInt(range_from) - 1;
//   gridApi.forEachNode(rowNode => {
//     if (index < minIndex){
//       index+=1;
//       return;
//     }
//     if (exists){
//       return;
//     }
//     if (gridApi.getCellValue(`${columnKey}-v`, rowNode) == 'error'){
//       exists = true;
//     }
//     index+=1;
//   });

//   return exists;
// };

const getDisplayedRows = (gridApi) => {
  let rowData = [];
  gridApi?.forEachNode((rowNode) => {
    if (rowNode.displayed) {
      rowData.push(rowNode.data)
    }
  });

  return rowData
};

const getDisplayedRowsToImport = (gridRef, datagrid) => {
  let rowData = [];
  let gridApi = gridRef.current.api;

  let columnAccessors = datagrid.columns.map(c => c.accessor);
  let colDefs = gridApi.getGridOption('columnDefs').filter(c => columnAccessors.includes(c.field))

  gridApi.forEachNode((rowNode) => {
    if (rowNode.displayed) {
      const row = {editable: true, imported: true};
      colDefs.forEach(colDef => {
        row[colDef.field] = colDef.valueGetter({data: rowNode.data});
      })

      rowData.push(row);
    }
  });

  return rowData
};

export {
  validateDataType,
  validateBlank,
  parseValue,
  flatMapDeep,
  valueParser,
  getInitialColumnDefs,
  getDisplayedRows,
  // isStepFailed,
  STATUSES,
  getDisplayedRowsToImport,
  numberFormatOptions
};
